import { Box, Text, VStack, HStack, Badge } from "@chakra-ui/react";

// Components
import CustomModal from "../../../../shared/Modals/CustomModal";

// Hooks
import { useReservations } from "../../../../hooks/useReservations";

interface RecoverReservationModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess: () => void;
	reservation: {
		id: number;
		analystName: string;
		passGroupId: string;
		isAllPassGroups: boolean;
		benefitCode: string;
		isAllBenefits: boolean;
	} | null;
}

/**
 * Confirmation modal for recovering a soft-deleted claim reservation
 */
export default function RecoverReservationModal({
	isOpen,
	onClose,
	onSuccess,
	reservation
}: RecoverReservationModalProps) {
	const { recoverReservation, isLoading } = useReservations();

	const handleConfirm = async () => {
		if (!reservation) return;

		const response = await recoverReservation(reservation.id);

		if (response) {
			onSuccess();
			onClose();
		}
	};

	if (!reservation) return null;

	return (
		<CustomModal
			isOpen={isOpen}
			isCentered={true}
			closeOnOverlayClick={false}
			onCancel={onClose}
			onContinue={handleConfirm}
			size="md"
			title="Recover Claim Reservation?"
			continueText="Recover"
			continueColorScheme="green"
			continueLoading={isLoading}
			content={
				<Box py={4}>
					<Text mb={4}>Are you sure you want to recover this auto-assignment rule?</Text>

					<VStack spacing={3} align="stretch" bg="gray.50" p={4} borderRadius="md">
						<HStack>
							<Text fontWeight="semibold" minW="120px">
								Analyst:
							</Text>
							<Text>{reservation.analystName}</Text>
						</HStack>

						<HStack>
							<Text fontWeight="semibold" minW="120px">
								Pass Group:
							</Text>
							{reservation.isAllPassGroups ? (
								<Badge colorScheme="blue" fontSize="xs">
									All Groups
								</Badge>
							) : (
								<Text>{reservation.passGroupId}</Text>
							)}
						</HStack>

						<HStack>
							<Text fontWeight="semibold" minW="120px">
								Benefit Code:
							</Text>
							{reservation.isAllBenefits ? (
								<Badge colorScheme="blue" fontSize="xs">
									All Benefits
								</Badge>
							) : (
								<Text>{reservation.benefitCode}</Text>
							)}
						</HStack>
					</VStack>

					<Text mt={4} fontSize="sm" color="gray.600">
						This will reactivate automatic claim assignments based on this rule.
					</Text>
				</Box>
			}
		/>
	);
}
